/**
 * Stone Investment Spaces CTA
 * 
 * AI Spaces promotion with Stone Investment's luxury aesthetic:
 * - Editorial styling
 * - Multiple variants (banner, card, inline)
 * - Cinematic transitions
 */

'use client';

import Link from 'next/link';
import { Wand2, Sparkles } from 'lucide-react';

export interface StoneSpacesCTAProps {
  /**
   * Display variant
   */
  variant?: 'banner' | 'card' | 'inline';
  /**
   * Current locale
   */
  locale?: string;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneSpacesCTA - AI Spaces promotion
 */
export function StoneSpacesCTA({
  variant = 'banner',
  locale = 'en',
  messages = {},
  className = '',
}: StoneSpacesCTAProps) {
  const spacesUrl = `/${locale}/spaces`;
  
  // Inline variant
  if (variant === 'inline') {
    return (
      <Link
        href={spacesUrl}
        className={`inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 text-white font-sans text-sm uppercase tracking-[0.15em] hover:bg-stone-800 transition-colors ${className}`}
      >
        <Wand2 size={16} strokeWidth={1.5} />
        <span>{messages.title || 'AI Room Design'}</span>
      </Link>
    );
  }
  
  // Card variant
  if (variant === 'card') {
    return (
      <div className={`bg-stone-100 p-8 ${className}`}>
        <div className="flex items-start gap-6">
          <div className="w-14 h-14 bg-stone-900 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-6 h-6 text-white" strokeWidth={1} />
          </div>
          <div className="flex-1">
            <h3 
              className="font-serif text-xl text-stone-900 mb-2"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {messages.reimagineSpace || 'Reimagine This Space'}
            </h3>
            <p className="text-stone-600 text-sm font-sans mb-6">
              {messages.transformDescription || 'Upload a photo and let AI transform it with stunning interior design styles.'}
            </p>
            <Link
              href={spacesUrl}
              className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white font-sans text-sm uppercase tracking-[0.15em] hover:bg-stone-800 transition-colors"
            >
              <Sparkles size={16} strokeWidth={1.5} />
              <span>{messages.tryAISpaces || 'Try AI Spaces'}</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Banner variant (default)
  return (
    <section className={`relative overflow-hidden bg-stone-900 ${className}`}>
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-stone-800/50 blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-stone-700/30 blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-24">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 border border-stone-700 flex items-center justify-center">
              <Wand2 className="w-8 h-8 text-stone-300" strokeWidth={1} />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 text-center lg:text-left">
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3 flex items-center justify-center lg:justify-start gap-2">
              <Sparkles size={12} />
              <span>{messages.aiPoweredDesign || 'AI-Powered Design'}</span>
            </p>
            <h2 
              className="font-serif text-3xl lg:text-4xl text-white mb-4"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {messages.transformTitle || 'Transform Any Space with AI'}
            </h2>
            <p className="text-stone-400 font-sans max-w-xl">
              {messages.description || 'Upload a photo of any room and watch as our AI reimagines it with stunning interior design styles. Visualize your dream home before making any changes.'}
            </p>
          </div>
          
          {/* CTA */}
          <div className="flex-shrink-0">
            <Link
              href={spacesUrl}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-stone-900 font-sans text-sm uppercase tracking-[0.15em] hover:bg-stone-100 transition-colors"
            >
              <span>{messages.tryAISpaces || 'Try AI Spaces'}</span>
              <svg 
                className="w-4 h-4 group-hover:translate-x-1 transition-transform" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default StoneSpacesCTA;
